import { Box, Typography } from '@mui/material';
import React, { useState } from 'react';
import ReactPlayer from 'react-player';

interface VideoPlayerProps {
  url: string;
  thumbnail?: string;
  getLocalizedText: (text: any, fallback?: string) => string;
}

/**
 * Detects the type of video URL
 */
const detectVideoType = (url: string): 'youtube' | 'vimeo' | 'direct' => {
  if (!url) return 'direct';

  const lowerUrl = url.toLowerCase();

  // Check for YouTube
  if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) {
    return 'youtube';
  }

  // Check for Vimeo
  if (lowerUrl.includes('vimeo.com')) {
    return 'vimeo';
  }

  // Check for direct video file (mp4, webm, ogg, etc.)
  const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv', '.flv', '.wmv'];
  const hasVideoExtension = videoExtensions.some(ext => lowerUrl.includes(ext));

  if (hasVideoExtension) {
    return 'direct';
  }

  // Default to direct (might be a streaming URL)
  return 'direct';
};

const VideoPlayer: React.FC<VideoPlayerProps> = ({ url, thumbnail, getLocalizedText }) => {
  const [playing, setPlaying] = useState(false);
  const [useNativePlayer, setUseNativePlayer] = useState(false);
  const [isReady, setIsReady] = useState(false); // Track if ReactPlayer is ready

  // Normalize YouTube URL to ensure ReactPlayer can handle it
  const normalizeYouTubeUrl = (url: string): string => {
    if (!url) return url;

    // Extract video ID from various YouTube URL formats
    // Handles: youtube.com/watch?v=ID, youtube.com/embed/ID, youtu.be/ID, etc.
    const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = url.match(youtubeRegex);

    if (match && match[1]) {
      const videoId = match[1];
      // Use watch URL format - ReactPlayer detects this better than embed
      // ReactPlayer automatically converts watch URLs to embed internally
      return `https://www.youtube.com/watch?v=${videoId}`;
    }

    // If already a valid YouTube URL, return as-is (but clean up timestamp)
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      // Remove timestamp parameter if present (can cause loading issues)
      return url.split('&t=')[0].split('#t=')[0];
    }

    return url;
  };

  // Build full URL for local files (similar to file download logic)
  const buildVideoUrl = (videoUrl: string): string => {
    if (!videoUrl || videoUrl.trim() === '') {
      return '';
    }

    // If it's a YouTube URL, normalize it first
    const lowerUrl = videoUrl.toLowerCase();
    if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) {
      return normalizeYouTubeUrl(videoUrl);
    }

    // If it's a Vimeo URL, use as-is
    if (lowerUrl.includes('vimeo.com')) {
      return videoUrl;
    }

    // If it's an absolute URL (http/https), use as-is
    if (videoUrl.startsWith('http://') || videoUrl.startsWith('https://')) {
      return videoUrl;
    }

    // For relative URLs, build the full URL with authentication
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
    const baseUrl = API_BASE_URL.replace('/api', '');

    if (videoUrl.startsWith('/')) {
      if (videoUrl.startsWith('/uploads/')) {
        return `${API_BASE_URL}${videoUrl}`;
      }
      return `${baseUrl}${videoUrl}`;
    } else {
      return `${API_BASE_URL}/uploads/${videoUrl}`;
    }
  };

  const fullVideoUrl = buildVideoUrl(url);
  const videoType = detectVideoType(fullVideoUrl);

  // For direct video files from external URLs (like CloudFront), use native player by default
  // ReactPlayer can have issues with some external video URLs
  const shouldUseNativePlayer = videoType === 'direct' &&
    (fullVideoUrl.startsWith('http://') || fullVideoUrl.startsWith('https://'));

  // Debug logs
  console.log('🎥 VideoPlayer props:', {
    originalUrl: url,
    fullVideoUrl,
    thumbnail,
    videoType,
    playing,
    shouldUseNativePlayer,
    useNativePlayer
  });

  // Additional check for YouTube URLs
  if (videoType === 'youtube') {
    console.log('🔍 YouTube URL detected, ReactPlayer should use YouTube player');
    console.log('   URL format check:', {
      hasWatch: fullVideoUrl.includes('/watch'),
      hasYoutube: fullVideoUrl.includes('youtube.com'),
      hasVideoId: fullVideoUrl.includes('v=')
    });
  }

  // If no URL, show placeholder
  if (!fullVideoUrl || fullVideoUrl.trim() === '') {
    console.warn('⚠️ VideoPlayer: No URL provided');
    return (
      <Box className="video-player video-player--no-url">
        <Typography variant="body2" className="video-player__placeholder-text">
          Aucune vidéo disponible
        </Typography>
      </Box>
    );
  }

  // Use native HTML5 video player ONLY for direct video files (not YouTube/Vimeo)
  // YouTube and Vimeo must use ReactPlayer
  if (videoType === 'direct' && (useNativePlayer || shouldUseNativePlayer)) {
    return (
      <Box className="video-player video-player--native">
        <video
          src={fullVideoUrl}
          controls
          className="video-player__native-video"
          onPlay={() => {
            console.log('▶️ Native video started playing');
            setPlaying(true);
          }}
          onPause={() => {
            console.log('⏸️ Native video paused');
            setPlaying(false);
          }}
          onError={(e) => {
            console.error('❌ Native video player error:', e);
          }}
          onLoadedMetadata={() => {
            console.log('✅ Native video metadata loaded');
          }}
        />
      </Box>
    );
  }

  // For YouTube videos, use direct iframe for better compatibility
  if (videoType === 'youtube') {
    // Extract video ID from URL
    const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = fullVideoUrl.match(youtubeRegex);
    const videoId = match ? match[1] : null;

    if (videoId) {
      const embedUrl = `https://www.youtube.com/embed/${videoId}?enablejsapi=1&origin=${encodeURIComponent(typeof window !== 'undefined' ? window.location.origin : '')}&autoplay=${playing ? 1 : 0}&controls=1&modestbranding=1&rel=0`;

      console.log('🎬 Rendering YouTube iframe directly with videoId:', videoId);

      return (
        <Box className="video-player video-player--youtube">
          <iframe
            src={embedUrl}
            width="100%"
            height="100%"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="video-player__iframe"
          />
        </Box>
      );
    }
  }

  // For Vimeo videos, use direct iframe for better compatibility
  if (videoType === 'vimeo') {
    // Extract video ID from Vimeo URL
    // Handles: vimeo.com/VIDEO_ID, vimeo.com/VIDEO_ID?param=value, player.vimeo.com/video/VIDEO_ID
    const vimeoRegex = /(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)/;
    const match = fullVideoUrl.match(vimeoRegex);
    const videoId = match ? match[1] : null;

    if (videoId) {
      const embedUrl = `https://player.vimeo.com/video/${videoId}?autoplay=${playing ? 1 : 0}&title=0&byline=0&portrait=0&responsive=1`;

      console.log('🎬 Rendering Vimeo iframe directly with videoId:', videoId);

      return (
        <Box className="video-player video-player--vimeo">
          <iframe
            src={embedUrl}
            width="100%"
            height="100%"
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            className="video-player__iframe"
          />
        </Box>
      );
    }
  }

  // Render video player for non-YouTube videos or before YouTube video starts
  console.log('🎬 Rendering ReactPlayer with url:', fullVideoUrl, 'playing:', playing, 'useNativePlayer:', useNativePlayer);

  return (
    <Box className="video-player video-player--react-player">
      {/* @ts-ignore - react-player config types are complex and vary by version */}
      <ReactPlayer
        url={fullVideoUrl}
        playing={playing} // Play when user clicks
        controls={true}
        width="100%"
        height="100%"
        light={false} // Don't use light mode - show player directly
        pip={false} // Disable picture-in-picture
        config={{
          // @ts-ignore
          youtube: {
            // @ts-ignore
            playerVars: {
              modestbranding: 1,
              rel: 0,
              autoplay: 0, // Don't autoplay (user must click)
              controls: 1,
              enablejsapi: 1,
              origin: typeof window !== 'undefined' ? window.location.origin : '' // Important for YouTube iframe API
            }
          },
          // @ts-ignore
          vimeo: {
            // @ts-ignore
            playerOptions: {
              responsive: true,
              title: false,
              byline: false,
              portrait: false
            }
          },
          file: {
            attributes: {
              controlsList: 'nodownload'
            }
          }
        }}
        onPlay={() => {
          console.log('▶️ Video started playing');
          setPlaying(true);
        }}
        onPause={() => {
          console.log('⏸️ Video paused');
          setPlaying(false);
          // Don't reset hasStarted - keep player visible when paused
        }}
        onError={(error: any) => {
          console.error('❌ ReactPlayer error:', error);
          // Fallback to native player for direct video files
          if (videoType === 'direct') {
            console.log('🔄 Falling back to native HTML5 video player');
            setUseNativePlayer(true);
          }
        }}
        onReady={() => {
          console.log('✅ ReactPlayer ready for:', videoType, 'URL:', fullVideoUrl);
          console.log('🔍 Checking if ReactPlayer detected YouTube correctly...');
          setIsReady(true);
        }}
        onStart={() => {
          console.log('▶️ ReactPlayer started');
          setIsReady(true);
        }}
        onProgress={(state: any) => {
          // Log progress for debugging (includes duration info)
          if (state.playedSeconds > 0 || state.loadedSeconds > 0) {
            console.log('⏱️ Video progress:', {
              played: state.playedSeconds,
              loaded: state.loadedSeconds,
              duration: state.playedSeconds > 0 ? 'available' : 'unknown'
            });
          }

          // If video is loaded but duration is still 0, ReactPlayer might not be able to play it
          // Only fallback to native player for direct video files (not YouTube/Vimeo)
          if (state.loaded > 0 && state.loadedSeconds === 0 && state.playedSeconds === 0 && videoType === 'direct') {
            console.log('⚠️ Video loaded but duration is 0, trying native player');
            setUseNativePlayer(true);
          }
        }}
        onEnded={() => {
          console.log('⏹️ Video ended');
          setPlaying(false);
        }}
      />
    </Box>
  );
};

export default VideoPlayer;
